//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    model,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DropDownPaginatedModel } from "../../pojo/DropDownPaginatedModel";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Defer, jSith } from "../../util/jquery-replacement";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25InfiniteScrollModule } from "../s25-infinite-scroll/s25.infinite.scroll.module";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { InfiniteScrollApi } from "../s25-infinite-scroll/infinite.scroll.api";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Bind } from "../../decorators/bind.decorator";
import { MasterDefTag } from "../../pojo/MasterDefTag";
import { FormValueControl } from "@angular/forms/signals";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { MultiselectResultsApi, MultiselectPopupApi } from "./s25.multiselect.api";
import { S25MultiselectItemComponent } from "./s25.multiselect.item.component";

export interface MultiselectDomainFilterI {
    options?: S25ItemI[]; //list of filters from which to choose (itemId used with prefix, itemName is display value)

    //WS param like "&query_id="
    //this is combined with chosen.itemId and used in filterMethod, which has the whole domainFilter in this
    prefix?: string;

    filterMethod: Function; //must return promise
    promise?: Promise<any>; //set by component: result of filterMethod
    chosen?: any; //set by component: chosen filter
}

export interface MultiselectCustomFilterI {
    isPushed?: boolean;
    label?: string;
    action?: (model: MultiselectModelI) => any;
}

export interface MultiselectModelI {
    items?: S25ItemI[];
    uuid?: string;
    origItems?: S25ItemI[]; //set by component
    selectedItems?: S25ItemI[]; //set by component
    uncommittedSelectedItems?: S25ItemI[]; //set by component and only used if hasCommit is true,
    removedItems?: S25ItemI[]; //set by component,
    addedItems?: S25ItemI[]; //set by component,
    title?: string;
    expensiveSearch?: boolean; //indicates if user must search to populate multiselect bc too many items could exist to pre-populate
    hasFilter?: boolean; //if user can filter results
    hasTags?: boolean; //If the user can filter on SystemTags
    tagTypeName?: MasterDefTag.types;
    hasCategories?: boolean;
    domainFilter?: MultiselectDomainFilterI;
    customFilter?: MultiselectCustomFilterI[];
    omitReadOnly?: boolean;
    customFilterValue?: string;
    showFavorite?: boolean;
    showResult?: boolean;
    showMatching?: boolean;
    filterFavorites?: boolean;
    singleSelect?: boolean;
    /** When set and non-empty, list/search results are limited to these item IDs (client-side). */
    allowedItemIds?: number[];
    hasSelectAll?: boolean;
    hasSelectNone?: boolean;
    hasDone?: boolean;
    hasCommit?: boolean;
    hasCancel?: boolean;
    hasQuantity?: boolean;
    hasScroll?: boolean;
    noSort?: boolean;
    preFetchItems?: boolean;
    isItemsInit?: boolean;
    isSearchCriteria?: boolean;
    commitErrorMsg?: string;
    addSelectedItem?: (item: S25ItemI) => any; //set by component
    removeSelectedItem?: (item: S25ItemI) => any; //set by component
    selectNone?: () => any;
    clearSearch?: () => any;
    serverSideFilter?: (searchTerm: string) => Promise<S25ItemI[]>;
    page?: number;
    pageCount?: number;
    pageSize?: number;
    cacheId?: number;
    filter?: any;
    hasMoreF?: () => boolean;
    scrollF?: () => Promise<{ pageCount: number; page: number; items: S25ItemI[] }>;
    dataPromise?: Promise<any>;
    dataDefer?: Defer;
    removeAction?: Function;
    addAction?: Function;
    onInit?: () => Promise<any>;
    onCommit?: Function;
    onDone?: () => void;
    onChange?: Function;
    action?: Function;
    matching?: "any" | "all";
    onMatchingChange?: (matching: "any" | "all") => any;
    extractItems?: (data: any) => S25ItemI[]; // Custom client side filtering of fetched items
    serviceMethod?: Function;
    currentPromises?: Promise<any>[];
    popoverTemplate?: TemplateRef<any>;
    popoverClass?: string;
    onShow?: Function; //on popover shown if multiselect is in popover
    onHide?: Function; //on popover hide if multiselect is in popover
    done?: Function; //set by component for outsiders to call
    cancel?: Function; //set by component for outsiders to call
    closing?: boolean; //if popover being closed manually
    isInline?: boolean; //if popover button is going to be inline next to other elements (if true, need to add a div above it with ngRelative class)
    textButton?: boolean; //if popover button should look like a text button vs regular outline button
    buttonText?: string; //optional text for the button
    placeholder?: string; //if adding input placeholder at smaller screen sizes to avoid label clutter
    buttonClass?: string; // optional css for the button,  such as online, primary
    usePopover?: boolean; //option to render multiselect with or without popover
}

@TypeManagerDecorator("s25-ng-multiselect")
@Component({
    selector: "s25-ng-multiselect",
    template: `
        @if (!modelBean.usePopover && !modelBean.domainFilter) {
            <div class="no-popover-spinner">
                <s25-loading-inline [model]="{}" />
            </div>
        }
        @if (isInit) {
            <div class="s25-ng s25-multiselect-popup-container" [id]="id">
                <div class="s25-header">
                    <div class="s25-title">{{ modelBean.title }}</div>
                    <s25-ng-icon-button
                        [type]="'close'"
                        (clicked)="done()"
                        [ariaLabel]="'Close Menu'"
                        [round]="true"
                        class="close-button"
                    />
                    @if (hasItems || modelBean.expensiveSearch) {
                        @if (modelBean.hasFilter) {
                            <div class="s25-search c-seriesQL-multiselect--input">
                                <input
                                    #filterInput
                                    [(ngModel)]="searchTerm"
                                    (ngModelChange)="searchByTerm()"
                                    class="c-input"
                                    type="text"
                                    aria-label="filter"
                                    [placeholder]="modelBean.placeholder || ''"
                                />
                                @if (searchTerm) {
                                    <s25-ng-icon-button
                                        class="clear-btn"
                                        [ariaLabel]="'Clear filter'"
                                        (clicked)="clearSearchTerm()"
                                        [type]="'close'"
                                        [round]="true"
                                    />
                                }
                            </div>
                        }
                        @if (modelBean.domainFilter && dropdownModel) {
                            <s25-dropdown-paginated
                                class="ngDomainFilterContainer"
                                [model]="dropdownModel"
                                [onSelect]="searchByTerm"
                                [(chosen)]="modelBean.domainFilter.chosen"
                                [choice]="choice"
                                [ariaLabel]="'Select a filter'"
                            >
                                <ng-template #choice let-item="item">
                                    <div [innerHTML]="item.itemName">
                                        @if (item.isInactive === 1) {
                                            <span class="not_active"> [Not Active] </span>
                                        }
                                    </div>
                                </ng-template>
                            </s25-dropdown-paginated>
                        }
                        @if (modelBean.hasTags && tagsList.length) {
                            <s25-ng-generic-multiselect-dropdown
                                [(value)]="chosenTags"
                                [items]="tagsList"
                                [searchEnabled]="true"
                                [placeholder]="'Filter By Tag'"
                                [resetSelectedOnCleanup]="true"
                                (valueChange)="searchByTerm()"
                                [hasDone]="false"
                            />
                        }
                        @if (modelBean.hasCategories) {
                            <s25-ng-dropdown-multi-search-criteria
                                [(value)]="chosenCategories"
                                (valueChange)="searchByTerm()"
                                [type]="'securityGroupCategories'"
                                [placeholder]="'Filter By Category'"
                            />
                        }
                        @if (
                            modelBean.showFavorite ||
                            (!modelBean.singleSelect && modelBean.hasSelectAll) ||
                            modelBean.hasSelectNone
                        ) {
                            <div class="aw-button-group">
                                @if (modelBean.showFavorite) {
                                    <button
                                        (click)="toggleFavorite()"
                                        class="btn ngToggleFavoriteBtn aw-button--outline aw-button"
                                        [class.ngToggleBtnPushed]="modelBean.filterFavorites"
                                    >
                                        <s25-ng-icon [type]="'starFilled'" [ariaLabel]="false" />
                                        Only Favorites
                                    </button>
                                }
                                @if (!modelBean.singleSelect && modelBean.hasSelectAll) {
                                    <button class="btn aw-button aw-button--outline" (click)="selectAll()">
                                        <s25-ng-icon [type]="'check'" [ariaLabel]="false" />
                                        Select All
                                    </button>
                                }
                                @if (modelBean.hasSelectNone) {
                                    <button class="btn aw-button aw-button--outline" (click)="selectNone()">
                                        <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                        Select None
                                    </button>
                                }
                            </div>
                        }
                        @if (modelBean.customFilter) {
                            <div class="custom-filters">
                                @for (filter of modelBean.customFilter; track $index) {
                                    <button
                                        (click)="filter.action!(modelBean)"
                                        class="s25-btn btn {{
                                            filter.isPushed ? 'ngToggleBtnPushed' : ''
                                        }} aw-button--outline aw-button"
                                    >
                                        {{ filter.label }}
                                    </button>
                                }
                            </div>
                        }
                    }
                </div>
                <div
                    class="s25-multiselect-columns-container"
                    s25-infinite-scroll
                    [onScroll]="scrollF"
                    [hasMorePages]="modelBean.hasMoreF"
                    topSelector="self"
                    [ready]="modelBean.dataPromise"
                >
                    <s25-loading-inline [model]="{}" />
                    <div
                        class="s25-multiselect-columns"
                        [class.s25-multiselect-columns-quantity]="modelBean.hasQuantity"
                    >
                        @for (item of modelBean.items; track $index) {
                            <div [hidden]="item.isSecret" class="s25-multiselect-infinite-scroll-item">
                                @if (modelBean.singleSelect) {
                                    <s25-ng-multiselect-item
                                        type="radio"
                                        (clear)="modelBean.selectNone()"
                                        (select)="modelBean.addSelectedItem(item)"
                                        (remove)="modelBean.removeSelectedItem(item)"
                                        (changed)="modelBean.onChange?.()"
                                        [hasQuantity]="modelBean.hasQuantity"
                                        [item]="item"
                                        [autoFocus]="!modelBean.hasFilter"
                                    />
                                }
                                @if (!modelBean.singleSelect) {
                                    <s25-ng-multiselect-item
                                        type="check"
                                        (clear)="modelBean.selectNone()"
                                        (select)="modelBean.addSelectedItem(item)"
                                        (remove)="modelBean.removeSelectedItem(item)"
                                        (changed)="modelBean.onChange?.()"
                                        [hasQuantity]="modelBean.hasQuantity"
                                        [item]="item"
                                        [autoFocus]="!modelBean.hasFilter"
                                    />
                                }
                            </div>
                        }
                        @if (!hasItems && !modelBean.expensiveSearch) {
                            <s25-ng-multiselect-item type="none" />
                        }
                    </div>
                </div>
                <div class="s25-multiselect-footer">
                    <div class="aw-button-group">
                        @if (modelBean.hasCommit && modelBean.hasCancel) {
                            <button class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                        }
                        @if (modelBean.hasDone) {
                            <button class="aw-button aw-button--primary" (click)="done()">Done</button>
                        }
                    </div>
                    @if (modelBean.commitErrorMsg) {
                        <div class="s25-error">{{ modelBean.commitErrorMsg }}</div>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        :host-context(.popover) {
            /* unless you can come with a clever way to set --bs-popover-font-size */
            font-size: 1rem;
        }

        .s25-header {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5em;
            padding: 0.5em;
            padding-right: 3em;
            position: relative;

            .s25-title {
                height: 2em;
                line-height: 2em;
                flex-basis: var(--multiselect--title-preferred-width, auto);
            }
            .close-button {
                position: absolute;
                top: 0.5em;
                right: 0.5em;
            }
            .s25-search {
                position: relative;
                flex: 1 1 16em;
                margin: 0;
            }
            /* noinspection CssOverwrittenProperties */
            .aw-button-group {
                flex: 1 1 auto;
                /* until flex-wrap balance is supported in all major browsers */
                flex-wrap: wrap;
                flex-wrap: wrap balance;
            }
            .ngDomainFilterContainer {
                display: block;
                flex: 1 1 300px;

                .ngDropdownPaginated {
                    margin-bottom: 0;
                }
            }

            @media screen and (width <= 700px) {
                padding-right: 0.5em;

                .s25-title {
                    padding-right: 3em;
                }
                > * {
                    flex-basis: 100%;
                }
                .s25-search {
                    max-width: unset;
                }
                .ngDropdownPaginated {
                    min-width: unset !important;
                }
            }
        }

        .aw-button-group {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            gap: 0.5em;
            margin: 0;
            padding: 0;

            > .aw-button {
                margin: 0;
                border-radius: 2px;
                white-space: nowrap;
            }
        }

        .custom-filters {
            display: flex;
            flex-wrap: nowrap;
            gap: 0.5em;
        }

        .ngToggleFavoriteBtn {
            --icon-size: 1em;
        }

        .c-seriesQL-multiselect--input {
            width: 100%;
        }
        .c-seriesQL-multiselect--input .c-input {
            padding-right: 1.75em;
            width: 100%;
        }
        .s25-multiselect-popup-container {
            width: var(--multiselect--preferred-width, 60vw);
        }

        .clear-btn {
            position: absolute;
            right: 0.375em;
            top: 0.375em;
            font-size: 0.75em;
        }

        s25-loading-inline {
            text-align: center;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        S25IconButtonComponent,
        FormsModule,
        S25DropdownPaginatedModule,
        S25IconComponent,
        S25InfiniteScrollModule,
        S25MultiselectItemComponent,
    ],
})
export class S25MultiselectComponent implements OnInit, OnChanges, FormValueControl<S25ItemI[]> {
    @Input() modelBean: MultiselectModelI;
    @Input() showResults: boolean;
    @Output() onDone: EventEmitter<S25ItemI[]> = new EventEmitter();

    // Selected items
    readonly value = model<S25ItemI[]>([]);

    private readonly filterInput = viewChild<ElementRef<HTMLInputElement>>("filterInput");
    private readonly destroyRef = inject(DestroyRef);

    isInit: boolean = false;
    hasItems: boolean;
    searchTerm: string;
    dropdownModel: DropDownPaginatedModel;
    #dirty: boolean = false;
    chosenTags: DropDownItem[] = [];
    tagsList: DropDownItem[] = [];
    chosenCategories: DropDownItem[] = [];
    private searchKey = 0;

    static count: number = 0;
    id: string;

    /*

     */
    searchByTerm = () => {
        this.searchKey++;
        const searchTerm = S25Util.toStr(this.searchTerm).toLowerCase();
        let startItems = this.modelBean.origItems;
        if (this.chosenTags?.length > 0) {
            this.searchByTag();
            startItems = this.modelBean.items;
        }

        if (this.chosenCategories?.length > 0) {
            this.searchByCategory();
            startItems = this.modelBean.items;
        }

        if (this.modelBean.hasScroll) {
            //if this is an expensive search that is empty, then just show selected items
            if (
                this.modelBean.expensiveSearch &&
                (!this.searchTerm || this.searchTerm.length === 0) && //empty search term
                !this.modelBean.filterFavorites && //not filtering favorites
                !this.modelBean.customFilterValue && //no custom filter value
                //no active domain filter (-999 means "All" so it is not an active filter)
                (!this.modelBean.domainFilter ||
                    !this.modelBean.domainFilter.chosen ||
                    this.modelBean.domainFilter.chosen.itemId == -999)
            ) {
                //code below reverts items shown to selected items list, falling back to empty array...
                this.modelBean.items = S25Util.array.clone(this.getSelectedItems()) || [];
                this.refresh();
                return jSith.when(this.modelBean.items);
            } else {
                S25LoadingApi.init(this.elementRef.nativeElement); //show loading for expensive search
                return this.modelBean.serverSideFilter(this.searchTerm).then(
                    (items) => {
                        //run server side filter
                        if (items) {
                            this.refreshSelected(items); //check new items, if needed
                            this.modelBean.items = items;
                            S25LoadingApi.destroy(this.elementRef.nativeElement); //hide loading after processing
                            this.refresh();
                            return jSith.when(this.modelBean.items);
                        } else {
                            S25LoadingApi.destroy(this.elementRef.nativeElement); //hide loading after processing
                            this.refresh();
                            return jSith.when();
                        }
                    },
                    () => {
                        S25LoadingApi.destroy(this.elementRef.nativeElement);
                        this.refresh();
                        return jSith.when();
                    },
                );
            }
        } else {
            this.modelBean.items = S25Util.array.clone(
                startItems.filter((item) => {
                    const itemString = S25Util.toStr(item.itemName).toLowerCase();
                    if (itemString.indexOf(searchTerm) > -1) {
                        //empty search term will include all items
                        if (this.modelBean.filterFavorites) {
                            return S25Util.toBool(item.isFav);
                        } else {
                            return true;
                        }
                    } else {
                        return false;
                    }
                }),
            );
            this.refreshSelected(this.modelBean.items);
        }
    };

    searchByCategory() {
        this.searchKey++;
        let items: any[] = [];
        this.chosenCategories.forEach((category) => {
            items = [...items, ...category.security_groups];
        });

        const uniqueItems: any[] = [];
        items.forEach((item: any) => {
            const found = uniqueItems.find((el) => el.security_group_id === item.security_group_id);
            if (!found) uniqueItems.push(item);
        });

        this.modelBean.items = uniqueItems.map((item: any) => {
            return {
                itemName: item.security_group_name,
                itemId: parseInt(item.security_group_id),
                status: item.status,
                checked: item.checked,
            };
        });

        this.modelBean.items.sort(this.itemSort);
        this.refreshSelected(this.modelBean.items);
    }

    /*
    Filters against any visible items. If an item has ANY tag it will be selected.
    When no tags are selected reset to original list
     */
    @Bind
    searchByTag(skipRefresh = true) {
        this.searchKey++;
        if (this.chosenTags?.length > 0) {
            this.modelBean.items = S25Util.array.clone(
                this.modelBean.origItems.filter((item) => {
                    for (let i = 0; i < this.chosenTags.length; i++) {
                        if (
                            S25Util.array.isIn(S25Util.array.forceArray(item.tags), "tagId", this.chosenTags[i].itemId)
                        ) {
                            return true;
                        }
                    }
                }),
            );
        } else {
            //reset to origial items
            this.modelBean.items = S25Util.array.clone(this.modelBean.origItems);
        }

        if (!skipRefresh) {
            this.refreshSelected(this.modelBean.items);
        }
    }

    /*
    When there are tags on the master definitions, collect all the tags on the visible items for use with filtering
     */
    getTagsByItems() {
        if (!this.modelBean?.hasTags) return;
        const tagIds = new Set();
        this.tagsList = [];
        this.modelBean.items?.forEach((item) => {
            S25Util.array.forceArray(item.tags).forEach((tag: MasterDefTag.MdTag) => {
                if (!tagIds.has(tag.tagId)) {
                    tagIds.add(tag.tagId);
                    this.tagsList.push({ itemName: tag.tagName, itemId: tag.tagId });
                }
            });
        });

        this.tagsList.sort((a, b) => (a.itemName || "").localeCompare(b.itemName || ""));
    }

    /** Clears only the text filter, leaving tag/category/favorite filters in place. */
    clearSearchTerm = () => {
        this.searchTerm = "";
        this.searchByTerm();
        this.filterInput()?.nativeElement.focus();
    };

    clearSearch = () => {
        this.searchKey++;
        this.modelBean.page = 0;
        this.modelBean.cacheId = 0;
        this.modelBean.filter = undefined;
        this.modelBean.filterFavorites = false;
        this.searchTerm = "";
        if (this.modelBean.domainFilter && this.modelBean.domainFilter.options) {
            this.modelBean.domainFilter.chosen = this.modelBean.domainFilter.options[0]; //All filter...
        }
        this.modelBean.customFilterValue = ""; //reset any custom filter query params that were added...
        if (this.modelBean.customFilter) {
            //reset any custom filter buttons to unpushed...
            jSith.forEach(this.modelBean.customFilter, (_: any, filter: MultiselectCustomFilterI) => {
                filter.isPushed = false;
            });
        }
        this.modelBean.items = S25Util.array.clone(this.modelBean.origItems);
        this.refreshSelected(this.modelBean.items);
    };

    getSelectedItems = (): S25ItemI[] => {
        return this.modelBean.hasCommit ? this.modelBean.uncommittedSelectedItems : this.modelBean.selectedItems;
    };

    refreshSelected = (items: S25ItemI[]) => {
        if (items && items.length) {
            const selectedItems = this.getSelectedItems();
            jSith.forEach(items, (_: any, item: S25ItemI) => {
                if (S25Util.array.isIn(selectedItems, "itemId", item.itemId)) {
                    //if item comes in checked or is pre-loaded to the checked list, set it
                    this.setSelected(item, true, false);
                } else {
                    this.setUnselected(item, true);
                }
            });
            this.sortSelectedItems(true);
        }
        this.refresh();
    };

    toggleFavorite = () => {
        const nextFilterFavorites = !this.modelBean.filterFavorites;
        this.clearSearch();
        this.modelBean.filterFavorites = nextFilterFavorites;
        return this.searchByTerm();
    };

    addAllPaginatedItems = () => {
        if (this.modelBean.hasScroll) {
            if (
                (this.searchTerm && this.searchTerm.length > 0) ||
                this.modelBean.filterFavorites ||
                this.modelBean.customFilterValue ||
                (this.modelBean.domainFilter &&
                    this.modelBean.domainFilter.chosen &&
                    [-999].indexOf(this.modelBean.domainFilter.chosen.itemId) === -1)
            ) {
                if (this.modelBean.hasMoreF()) {
                    S25LoadingApi.init(this.elementRef.nativeElement);
                    const data = { defer: jSith.defer() };
                    InfiniteScrollApi.addAllPages(this.elementRef.nativeElement, data);
                    return data.defer.promise.then(() => {
                        return setTimeout(() => {
                            S25LoadingApi.destroy(this.elementRef.nativeElement);
                        }, 250);
                    });
                } else {
                    return jSith.when();
                }
            } else {
                return jSith.when();
            }
        }
        return jSith.when();
    };

    setSelected = (item: S25ItemI, skipDetectChanges?: boolean, trackAsAdded = true) => {
        item.checked = true;
        const selectedItems = this.getSelectedItems();
        const notFound = S25Util.array.inplacePushByProp(selectedItems, "itemId", item.itemId, item);
        notFound &&
            trackAsAdded &&
            S25Util.array.inplacePushByProp(this.modelBean.addedItems, "itemId", item.itemId, item);
        S25Util.array.inplaceRemoveByProp(this.modelBean.removedItems, "itemId", item.itemId);

        const selectedItem = S25Util.array.getByProp(selectedItems, "itemId", item.itemId);
        item.isPermanent = selectedItem.isPermanent;

        //keep quantity
        if (this.modelBean.hasQuantity) {
            item.quantity = selectedItem.quantity;
            item.quantitySrc = item.quantity;
        }

        if (!skipDetectChanges) {
            this.refresh();
            this.#dirty = true;
        }
    };

    setUnselected = (item: S25ItemI, skipDetectChanges?: boolean) => {
        if (!item.isPermanent) {
            item.checked = false;
            const selectedItems = this.getSelectedItems();
            if (selectedItems.length > 0) {
                for (let i = selectedItems.length - 1; i >= 0; i--) {
                    //we need a nondestructive delete to maintain ref to calling directive, potentially
                    if (selectedItems[i].itemId == item.itemId) {
                        selectedItems[i].checked = false;

                        if (this.modelBean.hasQuantity) {
                            item.quantity = undefined;
                            item.quantitySrc = undefined;
                            selectedItems[i].quantity = undefined;
                            selectedItems[i].quantitySrc = undefined;
                        }

                        selectedItems.splice(i, 1);
                        S25Util.array.inplaceRemoveByProp(this.modelBean.addedItems, "itemId", item.itemId);
                        S25Util.array.inplacePushByProp(this.modelBean.removedItems, "itemId", item.itemId, item);
                        break;
                    }
                }
            }

            if (!skipDetectChanges) {
                this.refresh();
                this.#dirty = true;
            }
        }
    };

    sortSelectedItems = (skipDetectChanges?: boolean) => {
        if (!this.modelBean.noSort) {
            const selectedItems = this.getSelectedItems();
            selectedItems.sort(this.itemSort);
        }
        !skipDetectChanges && this.refresh();
    };

    selectAll = () => {
        this.addAllPaginatedItems().then(() => {
            jSith.forEach(this.modelBean.items, (_: any, item) => {
                this.setSelected(item, true);
            });
            this.sortSelectedItems(true);
            this.refresh();
            this.#dirty = true;
        });
        this.value.set([...(this.modelBean.selectedItems ?? [])]);
    };

    selectNone = () => {
        const selectedItems = this.getSelectedItems();
        for (let i = selectedItems.length - 1; i >= 0; i--) {
            this.setUnselected(selectedItems[i], true);
        }
        for (const item of this.modelBean.items) this.setUnselected(item, true); // Make sure all items are unselected
        this.refresh();
        this.#dirty = true;
        this.value.set([...(this.modelBean.selectedItems ?? [])]);
    };

    done = (skipPopupClose?: boolean) => {
        if (this.modelBean.hasScroll) {
            //reset scroll data promise so scroll waits for elements to start its polling and caching of elements
            InfiniteScrollApi.clear(this.elementRef.nativeElement);
            this.modelBean.dataDefer = jSith.defer();
            this.modelBean.dataPromise = this.modelBean.dataDefer.promise;
        }
        this.clearSearch(); //clear search

        if (this.modelBean.hasCommit) {
            this.modelBean.selectedItems = S25Util.array.clone(this.modelBean.uncommittedSelectedItems);
        }

        this.getSelectedItems().map(function (item) {
            S25Util.extend(item, {
                quantitySrc: item.quantity,
                checked: true,
            });
        });
        this.modelBean.action && this.modelBean.action();
        this.#dirty && this.modelBean.onChange && this.modelBean.onChange();
        this.modelBean.onDone && this.modelBean.onDone();
        const resp: any =
            (this.modelBean.hasCommit && this.modelBean.onCommit && this.modelBean.onCommit()) || jSith.when();
        resp &&
            resp.then &&
            resp.then(
                () => {
                    MultiselectResultsApi.refresh(document.body, this.modelBean.uuid);
                    !skipPopupClose && MultiselectPopupApi.close(document.body, this.modelBean.uuid);
                },
                (err: any) => {
                    this.modelBean.commitErrorMsg = S25Util.errorText(err);
                },
            );
        this.#dirty = false;
        this.value.set([...(this.modelBean.selectedItems ?? [])]);
        this.onDone.emit(this.modelBean.selectedItems);
    };

    cancel = (skipPopupClose?: boolean) => {
        if (this.modelBean.hasCommit) {
            //if has commit, then cancel means cancel (return to init state)
            this.isInit = false;
            this.refresh();
            this.zone.run(() => {
                this.modelBean.items = S25Util.array.clone(this.modelBean.origItems);
                this.modelBean.selectedItems = this.modelBean.items.filter((item) => {
                    return item.checked;
                });
                this.modelBean.removedItems = [];
                this.modelBean.addedItems = [];
                MultiselectResultsApi.refresh(document.body, this.modelBean.uuid);
                !skipPopupClose && MultiselectPopupApi.close(document.body, this.modelBean.uuid);
                this.ngOnInit();
            });
            this.#dirty = false;
        } else {
            //else it is just an alias for done()
            this.done(skipPopupClose);
        }
    };

    refresh = () => {
        try {
            this.cd.detectChanges();
        } catch (error: any) {}
    };

    initComp = () => {
        this.hasItems = this.modelBean.items && this.modelBean.items.length > 0;
        this.isInit = true;
        this.refreshSelected(this.modelBean.items);
    };

    forceScrollAction = () => {
        InfiniteScrollApi.forceScrollAction(this.elementRef.nativeElement);
    };

    itemSort = (a: S25ItemI, b: S25ItemI) => {
        const sortA = a.sortOrder ?? a.sort_order;
        const sortB = b.sortOrder ?? b.sort_order;
        if (S25Util.isDefined(sortA) && S25Util.isDefined(sortB)) {
            return sortA - sortB;
        }

        const nameA = String(a.itemName).toLowerCase();
        const nameB = String(b.itemName).toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        return 0;
    };

    @Bind
    protected async scrollF() {
        const searchKey = this.searchKey;
        const scrollResult = await this.modelBean.scrollF?.();
        if (this.destroyRef.destroyed || !scrollResult || searchKey !== this.searchKey) return;

        this.modelBean.pageCount = scrollResult.pageCount;
        this.modelBean.page = scrollResult.page;
        this.modelBean.items = scrollResult.items;
        this.refreshSelected(this.modelBean.items);
    }

    constructor(
        protected elementRef: ElementRef,
        protected cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        this.id = "select-" + S25MultiselectComponent.count++;

        effect(() => {
            const selectedItems = this.value();
            untracked(() => this.refreshSelected(selectedItems));
        });
    }

    async ngOnInit() {
        if (this.destroyRef.destroyed) return;
        if (this.showResults) this.modelBean.showResult = true;
        this.modelBean.uuid =
            this.modelBean.uuid || "multiselect-" + Date.now() + "-" + Math.floor(Math.random() * 10000);
        this.modelBean.hasFilter = S25Util.coalesce(this.modelBean.hasFilter, true);
        this.modelBean.hasDone = S25Util.coalesce(this.modelBean.hasDone, true);
        this.modelBean.onMatchingChange = this.modelBean.onMatchingChange || function (matching: "any" | "all") {};

        //cannot cancel unless we have a commit stage, bc as items were checked actions were getting fired on those items
        if (!this.modelBean.hasCommit) {
            this.modelBean.hasCancel = false;
        }

        this.modelBean.selectedItems = this.modelBean.selectedItems || [];
        this.modelBean.uncommittedSelectedItems = this.modelBean.hasCommit
            ? S25Util.array.clone(this.modelBean.selectedItems) || []
            : [];
        this.modelBean.removedItems = this.modelBean.removedItems || [];
        this.modelBean.addedItems = this.modelBean.addedItems || [];
        for (const item of this.value() ?? []) this.setSelected(item, true, false);
        this.value.set(this.modelBean.selectedItems);

        jSith.forEach(this.modelBean.selectedItems, (_: any, item: S25ItemI) => {
            if (!S25Util.array.isIn(this.modelBean.items, "itemId", item.itemId)) {
                this.modelBean.items.push(item);
            }
        });

        this.refreshSelected(this.modelBean.items); //with arrays set, fill 'em up
        this.modelBean.origItems = S25Util.array.clone(this.modelBean.items);

        this.modelBean.clearSearch = this.clearSearch;
        this.modelBean.addSelectedItem = (item: S25ItemI) => {
            this.setSelected(item);
            this.sortSelectedItems();
            this.value.set([...(this.modelBean.selectedItems ?? [])]);
            //note: do NOT perform scope.action() here; some action callers fire DAO changes not appropriate for each multiselect change
            this.modelBean.addAction && this.modelBean.addAction(item); //single item added to selections, fire addAction
        };
        this.modelBean.removeSelectedItem = (item: S25ItemI) => {
            this.setUnselected(item);
            this.value.set([...(this.modelBean.selectedItems ?? [])]);
            //note: do NOT perform scope.action() here; some action callers fire DAO changes not appropriate for each multiselect change
            this.modelBean.removeAction && this.modelBean.removeAction(item); //single item removed from selections, fire removeAction
        };
        this.modelBean.selectNone = () => this.selectNone(); // Needed for radio buttons
        this.modelBean.done = this.done;
        this.modelBean.cancel = this.cancel;

        const domainFilterPromise = (this.modelBean.domainFilter?.promise || jSith.when()).then(() => {
            this.dropdownModel = {
                placeholder: "Select a filter...",
                itemNameProp: "itemName",
                searchEnabled: true,
                items: this.modelBean.domainFilter && this.modelBean.domainFilter.options,
            };

            this.getTagsByItems();
        });

        this.modelBean.onInit =
            this.modelBean.onInit ||
            function () {
                return jSith.when();
            };

        const modelBean = this.modelBean;
        domainFilterPromise.then(this.modelBean.onInit).then(() => {
            if (this.destroyRef.destroyed || this.modelBean !== modelBean) return;

            // when pre populated chosen itemId to -999, need to display items  when first popup without click on All filtered option
            if (this.modelBean.items.length === 0 && this.modelBean.domainFilter?.chosen?.itemId === -999) {
                this.modelBean.items = this.modelBean?.origItems || [];
            }

            this.getTagsByItems();
            this.refreshSelected(this.modelBean.items); //do this again as onInit may have added items
            this.initComp();
        });

        if (!this.modelBean.usePopover && this.modelBean.isSearchCriteria && !this.modelBean.domainFilter) {
            const loadingEl = this.elementRef.nativeElement.querySelector(".no-popover-spinner");
            loadingEl && S25LoadingApi.init(loadingEl);

            await this.modelBean.onInit?.();

            if (this.destroyRef.destroyed) return;
            loadingEl && S25LoadingApi.destroy(loadingEl);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.modelBean) {
            this.ngOnInit(); // Re-init when the modelBean changes
        }
    }
}
